/**
 * Typed client for the agent runtime's HTTP API (`/agent/v2`). Shapes follow
 * lighthouse-backend docs/harness-v2/project2/API.md plus the Project 3 additions (queue, attachments,
 * autonomy, memory, goals).
 *
 * The transport is injectable: the app uses the authenticated axios client; tests pass a fake.
 */
import type { AdminEventsQuery, AdminFeedEvent, AdminGoal, AdminGoalFire, AdminJob, AdminJobDetail, AdminOverview, AdminPrompt, AdminRun, AdminRunsQuery, AdminRunsSummary, AdminStats, AdminTrace } from './adminTypes'
import type { AttachmentPayload, BrowserTaskCard, ClientCallAction, ClientCallStatus, EmailCard, JobStatus, LinkCard, PayloadMap, RunStatus, SuggestionChip, TodoItem } from './protocol/types'
import type { ScreenContext } from './screen/types'

export type * from './adminTypes'

export interface Session {
    id: string
    title: string
    status: string
    surface?: string | null
    initiated_by?: string | null
    unread?: boolean
    /** When the viewer pinned it to the top of the conversations sidebar (null / absent = not pinned). */
    pinned_at?: string | null
    /** Who started the chat (null: Lighthouse, or a chat from before workspace members). A chat is private to them
     *  unless it's shared with the workspace (`shared_at`) or they @mention someone. */
    created_by?: string | null
    created_by_name?: string | null
    shared_at?: string | null
    shared_by?: string | null
    created_at: string
    updated_at: string
}

export interface TranscriptMessage {
    id: string
    seq: number
    role: 'user' | 'assistant'
    text: string
    created_at: string
    suggestions?: SuggestionChip[]
    browser_task?: BrowserTaskCard
    /** The creator's attached files, with the run their message started. */
    attachments?: AttachmentPayload[]
    run_id?: string
    /** The creator's messages: spoken on a voice call, or typed (newer backends). */
    input_mode?: 'text' | 'voice'
    /** Agent-started messages about an email (the inbox): the email card and the links to open. */
    email?: EmailCard
    links?: LinkCard[]
    /** Who sent a user message (a teammate, in a shared chat) and the teammates it @mentioned. */
    sent_by?: string
    mentions?: { user_id: string; name: string }[]
    /** Said on a voice call by the call's voice (the talker), not written by the agent. */
    spoken?: boolean
}

/** The call's voice between the agent's replies: the server's talker, or a realtime speech-to-speech session. */
export type VoiceTalker = 'server' | 'realtime'

/** `GET /voice/config`. */
export interface VoiceConfig {
    talker: 'server' | 'off'
    steer: boolean
    realtime: boolean
    realtime_model?: string
}

export interface VoiceTimingBody {
    run_id: string
    marks: Record<string, number>
    pipeline: 'classic' | 'realtime'
    stt?: 'native' | 'web' | 'fake' | 'realtime'
    talker?: VoiceTalker | 'off'
    steered?: boolean
}

/** Someone @mentioned in a chat (`GET /sessions/:id`). */
export interface ChatMention {
    user_id: string
    name: string
    mentioned_by: string | null
    message_seq: number | null
    created_at: string
    seen_at: string | null
    resolved_at: string | null
}

/** `POST /sessions/:id/calls`: a voice call started or ended (emits `call.updated`). */
export interface CallUpdate {
    call_id: string
    status: 'started' | 'ended'
    duration_ms?: number
    turns?: number
    reason?: string
}

export type BrowserTaskStatus = 'proposed' | 'started' | 'waiting_creator' | 'completed' | 'cancelled' | 'failed'

/** A web task the agent proposed (app/harness/browser/tasks.py `view`). */
/** `email_drafts.variants`: every version of a reply draft; `body` is the selected one. */
export interface DraftVariants {
    recommended: string
    selected: string
    items: { key: string; label: string; why?: string; body: string; perspective?: string }[]
}

export interface BrowserTask {
    id: string
    task_id: string
    title: string
    url: string
    domain: string
    status: BrowserTaskStatus
    source: 'inbox' | 'chat'
    session_id: string | null
    message_id: string | null
    thread_id: string | null
    project_id: string | null
    plan: { why?: string; inputs?: { type: 'file' | 'field'; ref?: string; name?: string; size?: number; key?: string; value_source?: string }[]; needs_from_creator?: string[]; due?: string | null }
    signals: string[]
    suspicious: boolean
    result: { summary?: string; proof_file_id?: string; final_url?: string; finished_by?: string }
    created_at: string
    started_at: string | null
    finished_at: string | null
}

export type BrowserState = PayloadMap['browser.state']

/** GET /sessions/:id/browser: the live browser plus a fresh live-view URL (a secret: never store or log it). */
export interface BrowserView extends BrowserState {
    task: BrowserTask | null
    /** The browser's size (fixed for the session; shaped like the pane it was started in). */
    viewport?: { width: number; height: number }
    live_url?: string
    pages?: { id: string; url: string; title: string; live_url: string }[]
    live_error?: string
}

export interface Run {
    id: string
    session_id: string | null
    kind: string
    status: RunStatus
    tier: string
    model: string | null
    rounds: number
    tool_calls: number
    changeset_id: string | null
    error: string | null
    todo: TodoItem[]
    pending_question: { id: string; question: string; options?: string[] } | null
    created_at: string
    finished_at: string | null
}

export interface SessionDetail extends Session {
    messages: TranscriptMessage[]
    runs: Run[]
    last_event_id: number
    /** The creator's thumbs on agent replies, by message id. */
    feedback?: Record<string, 'up' | 'down'>
    /** Browser tasks in this conversation and the live browser's state (when browser tasks are on). */
    browser_tasks?: BrowserTask[]
    browser?: BrowserState
    /** The email thread this conversation is about (its own, or via a linked invoice / agreement / project). */
    linked_thread?: string | null
    /** Every thread it's about, in the order they joined it (the first is `linked_thread`). */
    linked_threads?: string[]
    /** The brand it's about (for the header logo). */
    linked_brand?: { id: string; name: string; domain: string | null } | null
    /** Teammates @mentioned here (newest first), and the viewer's own open mention. */
    mentions?: ChatMention[]
    my_mention?: ChatMention | null
    /** The viewer started this chat or owns the workspace: they may share it / stop sharing it. */
    can_share?: boolean
}

export interface ChangesetEffect {
    seq: number
    kind: string
    summary: string
    risk: string
    status: string
    reversible: boolean
    requires_approval_reason: string | null
    error: string | null
}

/** A big change in a few lines (more than 30 lines or effects): rules-built, or a model's rewrite of them. */
export interface ChangesetDigest {
    lines: string[]
    recap: string
    /** How many changes it stands for. */
    total: number
    by: 'rules' | 'model'
}

/** One reason some changes didn't go through (or were skipped), with how many and a few examples. */
export interface ProgressReason {
    reason: string
    count: number
    samples: string[]
}

/**
 * How far an approved change has got leaving Lighthouse (its effects: mailbox, calendar, …).
 * `state`: applying (some still to run), done, partial (some failed), failed (none went through).
 */
export interface ChangesetProgress {
    state: 'applying' | 'done' | 'partial' | 'failed' | 'cancelled' | 'none'
    total: number
    done: number
    skipped: number
    failed: number
    queued: number
    running: number
    /** Waiting out a provider's rate limit or a retry backoff. */
    waiting: number
    finished: number
    /** Failed ones Lighthouse undid on its side too (so it still matches the mailbox). */
    reverted?: number
    eta_s: number | null
    failures: ProgressReason[]
    skips: ProgressReason[]
    updated_at: string | null
    changeset_status?: string
}

export interface Changeset {
    id: string
    kind: string
    status: string
    /** Every line, or for a big change its digest's lines (`summary_total` says how many lines there are). */
    summary: string[]
    summary_total?: number
    digest?: ChangesetDigest | null
    affected: Record<string, number>
    /** All its ops (a big change's `ops` list stops at 1,000). */
    ops_count: number
    effects_total?: number
    progress?: ChangesetProgress | null
    max_risk: string | null
    policy: { decision?: string; reasons?: string[] } | null
    undo_of: string | null
    /** false once undo can no longer apply (row edited since, or past the undo window); null/absent = unknown. */
    undoable?: boolean | null
    effects: ChangesetEffect[]
    not_undone?: string[]
    /** Seqs of its ops whose records were put back one at a time (`undoRecords`). */
    undone?: number[]
    created_at?: string | null
    applied_at?: string | null
}

export interface Job {
    job_id: string
    title: string
    kind: string
    status: JobStatus
    total: number
    done: number
    failed: number
    skipped: number
    review: boolean
    concurrency: number
    error: string | null
    session_id: string | null
    created_at: string
    finished_at: string | null
}

export interface ReviewItem {
    id: string
    job_id: string | null
    title: string
    summary: string[]
    kind: string
    status: 'pending' | 'approved' | 'rejected' | 'skipped' | 'failed'
    similarity_key: string | null
    changeset_id: string | null
    payload: { item?: unknown; result?: unknown; max_risk?: string | null }
    error: string | null
    created_at: string
}

export interface ReviewGroup {
    similarity_key: string
    count: number
    example: string
}

export interface QueueItem {
    session_id: string
    title: string
    headline: string | null
    kind: 'needs_you' | 'fyi'
    urgency: string
    unread: boolean
    status: string
    snippet: string | null
    producer: string
    entity_refs: { entity_type: string; entity_id: string }[]
    snoozed_until: string | null
    pending_approval: boolean
    waiting_question: boolean
    /** Why the inbox put it here (newer backends): suspicious, deadline_risk, agreement_review, approval, … */
    reason_code?: string | null
    /** The thread is an active negotiation (a rate, scope or terms on the table). */
    negotiation?: boolean
    /** Ranking among "needs you" items (higher first). */
    priority?: number
    updated_at: string
    /** One or two word chip naming what the conversation is about ("Negotiation"), once generated. */
    premise_label?: string | null
    initiated_by?: string | null
    /** A teammate @mentioned the viewer here: it sits on top of their queue until they reply or dismiss it. Its
     *  `unread` is the viewer's own (opening it marks the mention seen). */
    mention?: { by: string | null; by_name: string; at: string; seen: boolean } | null
}

export interface AutonomyRule {
    risk: string
    label: string
    decision: 'auto_apply' | 'requires_approval' | 'forbidden'
    overridden: boolean
}

export interface AutonomyView {
    bulk_threshold: number
    rules: AutonomyRule[]
    changeset_id?: string | null
}

export interface MemoryFile {
    path: string
    size?: number
    updated_at?: string | null
    source?: string
}

export interface UploadedFile {
    file_id?: string
    filename?: string
    content_type?: string | null
    size?: number
    error?: string
}

/**
 * The app a message is sent from (only the desktop app sends one): its `capabilities` give the turn the
 * `desktop` tool, and `client_id` (one per window) is the window that answers the agent's asks.
 */
export interface ClientInfo {
    kind: 'desktop'
    client_id: string
    platform?: string | null
    app_version?: string | null
    capabilities: ClientCallAction[]
}

/** The desktop window's answer to a `client.call`. Nothing but `file_id` / `result` carries what was shared. */
export interface ClientCallAnswer {
    status: 'completed' | 'denied' | 'failed'
    client_id: string
    /** capture_screen: the screenshot, uploaded to the conversation's attachments first. */
    file_id?: string
    result?: { text?: string; opened?: boolean }
    error?: string
}

export type QueueAction = 'snooze' | 'unsnooze' | 'dismiss' | 'read' | 'unread' | 'resolve'

export class AgentApiError extends Error {
    constructor(
        message: string,
        public status: number,
        public code?: string,
    ) {
        super(message)
    }
}

export interface Transport {
    request<T>(method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE', path: string, opts?: { body?: unknown; params?: Record<string, unknown> }): Promise<T>
}

/** The app's authenticated axios client (lazily imported so tests never load Supabase). */
export const httpTransport: Transport = {
    async request(method, path, opts) {
        // Before the trial the runtime only answers 403: don't ask (or fill the console) while onboarding.
        const { agentRuntimeBlocked } = await import('@/onboarding/agentAccess')
        if (agentRuntimeBlocked()) {
            const { isCompanionApp } = await import('@/native/companion')
            throw new AgentApiError(isCompanionApp() ? 'Not available yet.' : 'Available once your trial starts.', 402, 'trial_required')
        }
        // Support view (staff impersonation without email access): the agent quotes email, so the backend answers
        // 403 `support_view_email_hidden`. Don't ask; the surfaces show a placeholder instead.
        const supportView = await import('@/utils/supportView')
        if (supportView.isSupportViewLimited()) {
            throw new AgentApiError(supportView.SUPPORT_VIEW_EMAIL_HIDDEN_MESSAGE, 403, supportView.SUPPORT_VIEW_EMAIL_HIDDEN)
        }
        const { default: client } = await import('@/api/auth-http-client')
        const res: any = await client.request({ method, url: `/agent/v2${path}`, data: opts?.body, params: opts?.params })
        // The app's interceptor resolves (not rejects) with the error response; network failures → {status: 500},
        // or {status: 0, offline: true} when the device is offline.
        const status = res?.status ?? 500
        if (status === 0) throw new AgentApiError("You're offline. Lighthouse will catch up when you reconnect.", 0, 'offline')
        if (status >= 400) {
            const d = res?.data || {}
            const err = d.error || (typeof d.detail === 'object' ? d.detail : null) || {}
            if (supportView.isSupportViewEmailHiddenResponse(status, d)) {
                throw new AgentApiError(supportView.SUPPORT_VIEW_EMAIL_HIDDEN_MESSAGE, status, supportView.SUPPORT_VIEW_EMAIL_HIDDEN)
            }
            const message = err.message || (typeof d.detail === 'string' ? d.detail : '') || `Request failed (${status})`
            throw new AgentApiError(message, status, err.code)
        }
        return res.data
    },
}

export function createAgentApi(t: Transport = httpTransport) {
    const get = <T>(path: string, params?: Record<string, unknown>) => t.request<T>('GET', path, { params })
    const post = <T>(path: string, body?: unknown) => t.request<T>('POST', path, { body: body ?? {} })
    return {
        transport: t,
        // sessions & messages
        createSession: (b: { title?: string; entity_refs?: { type: string; id: string }[]; surface?: string } = {}) =>
            post<Session>('/sessions', b),
        listSessions: (entity?: { type: string; id: string }) =>
            get<{ sessions: Session[] }>('/sessions', entity ? { entity_type: entity.type, entity_id: entity.id } : undefined),
        getSession: (id: string) => get<SessionDetail>(`/sessions/${id}`),
        /** Pin or unpin a chat in the viewer's sidebar (doesn't touch its place by recency). */
        pinSession: (id: string, pinned: boolean) => post<Session>(`/sessions/${id}/${pinned ? 'pin' : 'unpin'}`),
        /** Share a chat with the workspace, or stop (its creator or the workspace owner only: 403 otherwise). */
        shareSession: (id: string, shared: boolean) => post<Session>(`/sessions/${id}/${shared ? 'share' : 'unshare'}`),
        /** `mentions`: teammates the composer's @ picker inserted (user ids); they're pinged and see the chat. */
        /** `steer`: said while the session's run works (a voice call): the run takes it mid-task (`steered`), or the call's
         *  voice answers a question about the work itself (`answered`); with nothing running it's a new turn. */
        sendMessage: (id: string, b: { text: string; screen_context?: ScreenContext | null; tier?: string; attachments?: AttachmentPayload[];
                                       browser_pane?: { width: number; height: number } | null; input_mode?: 'text' | 'voice'; client?: ClientInfo;
                                       mentions?: string[]; steer?: boolean; voice_talker?: VoiceTalker }) =>
            post<Run & { steered?: boolean; answered?: string; message_id?: string }>(`/sessions/${id}/messages`,
                b.browser_pane ? { ...b, browser_pane: withTimezone(b.browser_pane) } : b),
        answer: (id: string, b: { text: string; question_id?: string; attachments?: AttachmentPayload[]; input_mode?: 'text' | 'voice'; client?: ClientInfo;
                                  mentions?: string[]; voice_talker?: VoiceTalker }) =>
            post<Run>(`/sessions/${id}/answer`, b),
        /** Record a voice call starting / ending (older backends 404: callers ignore failures). */
        postCall: (id: string, b: CallUpdate) => post<{ ok?: boolean }>(`/sessions/${id}/calls`, b),
        /** What calls can use on this server: the talker, steering, the realtime voice (older backends 404). */
        voiceConfig: () => get<VoiceConfig>('/voice/config'),
        /** One spoken turn's timeline (ms after the creator stopped talking), for the latency report. */
        voiceTiming: (b: VoiceTimingBody) => post<{ ok: boolean }>('/voice/timing', b),
        /** Start a realtime voice call: the phone's WebRTC offer in, the answer and the call id out (503: use classic). */
        realtimeConnect: (b: { session_id: string; sdp: string }) =>
            post<{ sdp: string; call_id: string; model: string }>('/voice/realtime/connect', b),
        realtimeHangup: (callId: string) => post<{ ended: boolean }>(`/voice/realtime/${callId}/hangup`),
        /** The desktop window answers the agent's ask to use the creator's computer (409 `client_call_closed` once
         *  it was answered, expired or cancelled). */
        resolveClientCall: (sessionId: string, callId: string, b: ClientCallAnswer) =>
            post<{ call_id: string; status: ClientCallStatus }>(`/sessions/${sessionId}/client-calls/${callId}`, b),
        messageFeedback: (id: string, b: { message_id: string; rating: 'up' | 'down' | null; run_id?: string | null; notes?: string }) =>
            post<{ message_id: string; rating: 'up' | 'down' | null }>(`/sessions/${id}/feedback`, b),
        pollEvents: (id: string, after = 0) => get<{ events: any[] }>(`/sessions/${id}/events/poll`, { after }),
        // browser tasks and the live browser
        getBrowserTask: (id: string) => get<BrowserTask>(`/browser-tasks/${id}`),
        /** `pane`: the space the live view has (CSS px); the browser is created in that shape, in the region
         *  nearest the creator (their timezone rides along). */
        startBrowserTask: (id: string, pane?: { width: number; height: number }) =>
            post<{ task: BrowserTask; run: Run; session_id: string }>(`/browser-tasks/${id}/start`,
                                                                      { ...(pane ? { pane } : {}), timezone: viewerTimezone() }),
        cancelBrowserTask: (id: string) => post<BrowserTask>(`/browser-tasks/${id}/cancel`),
        // reply draft versions (a "draft_variant" chip): switch the draft, no agent run. `held` is what a composer that
        // already switched on screen held: the version it `left` with its text (`kept`), and the one it shows (`body`).
        useDraftVariant: (draftId: string, key: string, held?: { kept?: string; body?: string; left?: string }) =>
            post<{ draft_id: string; selected: string; variants: DraftVariants | null; changeset_id: string | null }>(
                `/drafts/${draftId}/variants/${encodeURIComponent(key)}`, held),
        getBrowser: (sessionId: string) => get<BrowserView>(`/sessions/${sessionId}/browser`),
        takeBrowser: (sessionId: string) => post<BrowserState & { paused_run_id: string | null }>(`/sessions/${sessionId}/browser/take`),
        releaseBrowser: (sessionId: string, note?: string) => post<BrowserState & { run: Run | null }>(`/sessions/${sessionId}/browser/release`, note ? { note } : {}),
        /** The live view is on screen (keeps an unwatched browser from lingering: the reaper releases those fast). */
        /** Reshape the live browser to the stage the viewer actually has, so the live view fills the pane. */
        fitBrowser: (sessionId: string, stage: { width: number; height: number }) =>
            post<{ viewport: { width: number; height: number } }>(`/sessions/${sessionId}/browser/fit`, stage),
        browserPresence: (sessionId: string, focused: boolean) => post<{ status: string }>(`/sessions/${sessionId}/browser/presence`, { focused }),
        closeBrowser: (sessionId: string) => post<BrowserState>(`/sessions/${sessionId}/browser/close`),
        forgetBrowserLogins: () => t.request<{ deleted: boolean }>('DELETE', '/browser/profile'),
        // runs
        getRun: (id: string) => get<Run>(`/runs/${id}`),
        cancelRun: (id: string) => post<Run>(`/runs/${id}/cancel`),
        // changesets
        getChangeset: (id: string, ops = false) => get<Changeset>(`/changesets/${id}`, ops ? { ops: true } : undefined),
        /** A one-sentence, model-written recap of the changeset (may be null if unavailable). */
        getChangesetRecap: (id: string) => get<{ id: string; recap: string | null; digest?: ChangesetDigest | null }>(`/changesets/${id}/recap`),
        /** Where an approved change's outside effects stand; cheap enough to poll. */
        getChangesetProgress: (id: string) => get<ChangesetProgress & { id: string }>(`/changesets/${id}/progress`),
        changesetAction: (id: string, action: 'approve' | 'reject' | 'undo') => post<Changeset>(`/changesets/${id}/${action}`),
        /** Put back only some records of an applied change (their ops' seqs). Answers with the change itself. */
        undoRecords: (id: string, seqs: number[]) => post<Changeset>(`/changesets/${id}/undo`, { seqs }),
        // jobs
        listJobs: (status?: string) => get<{ jobs: Job[] }>('/jobs', status ? { status } : undefined),
        getJob: (id: string) => get<Job>(`/jobs/${id}`),
        jobAction: (id: string, action: 'pause' | 'resume' | 'cancel') => post<Job>(`/jobs/${id}/${action}`),
        jobEvents: (id: string, after = 0) => get<{ events: any[] }>(`/jobs/${id}/events`, { after }),
        // review queue
        listReview: (p: { job_id?: string; status?: string; limit?: number } = {}) =>
            get<{ items: ReviewItem[]; groups: ReviewGroup[] }>('/review', p),
        reviewAction: (id: string, action: 'approve' | 'reject' | 'skip' | 'edit', edits?: unknown[]) =>
            post<ReviewItem>(`/review/${id}/${action}`, edits ? { edits } : undefined),
        approveMany: (ids: string[]) => post<{ approved: number; failed: number }>('/review/approve', { ids }),
        approveSimilar: (similarity_key: string) =>
            post<{ approved: number; failed: number }>('/review/approve-similar', { similarity_key }),
        // queue (agent inbox)
        getQueue: (include_snoozed = false) =>
            get<{ items: QueueItem[]; counts: { needs_you: number; fyi: number; unread: number } }>('/queue', include_snoozed ? { include_snoozed: true } : undefined),
        queueAction: (sessionId: string, action: QueueAction, b: { until?: string; reason?: string } = {}) =>
            post<Session & { unread: boolean }>(`/sessions/${sessionId}/${action}`, b),
        // attachments (multipart; the transport passes FormData through untouched)
        uploadAttachments: (sessionId: string, files: File[]) => {
            const form = new FormData()
            for (const f of files) form.append('files', f)
            return t.request<{ files: UploadedFile[] }>('POST', `/sessions/${sessionId}/attachments`, { body: form })
        },
        // trust settings
        getAutonomy: () => get<AutonomyView>('/settings/autonomy'),
        putAutonomy: (b: { bulk_threshold?: number; rules?: Record<string, string> }) =>
            t.request<AutonomyView>('PUT', '/settings/autonomy', { body: b }),
        listMemory: (prefix = '/memory/') => get<{ files: MemoryFile[] }>('/memory', { prefix }),
        readMemory: (path: string) => get<{ path: string; content: string }>('/memory/file', { path }),
        writeMemory: (path: string, content: string) =>
            t.request<{ path: string; changeset_id: string | null }>('PUT', '/memory/file', { body: { path, content } }),
        // standing goals
        listGoals: () => get<{ goals: Goal[]; events: string[] }>('/goals'),
        createGoal: (b: GoalInput) => post<Goal & { changeset_id: string | null }>('/goals', b),
        updateGoal: (id: string, b: GoalInput) => t.request<Goal & { changeset_id: string | null }>('PATCH', `/goals/${id}`, { body: b }),
        deleteGoal: (id: string) => t.request<{ id: string; deleted: boolean }>('DELETE', `/goals/${id}`),
        goalAction: (id: string, action: 'pause' | 'resume' | 'run') => post<Goal & { fire?: GoalFire | null }>(`/goals/${id}/${action}`),
        goalHistory: (id: string, limit = 10) => get<{ fires: GoalFire[] }>(`/goals/${id}/history`, { limit }),
        // staff observability (Lightbase internal users only; see adminTypes.ts)
        adminRuns: (p: AdminRunsQuery = {}) =>
            get<{ runs: AdminRun[]; next_before: string | null; summary?: AdminRunsSummary }>('/admin/runs', p as Record<string, unknown>),
        adminTrace: (runId: string) => get<AdminTrace>(`/admin/runs/${runId}/trace`),
        adminPrompt: (runId: string) => get<AdminPrompt>(`/admin/runs/${runId}/prompt`),
        adminOverview: (p: { org?: string } = {}) => get<AdminOverview>('/admin/overview', p),
        adminEvents: (p: AdminEventsQuery = {}) =>
            get<{ events: AdminFeedEvent[]; last_id: number | null }>('/admin/events', p as Record<string, unknown>),
        adminStats: (p: { days?: number; org?: string } = {}) => get<AdminStats>('/admin/stats', p),
        adminGoals: (p: { org?: string; kind?: string; enabled?: boolean } = {}) => get<{ goals: AdminGoal[] }>('/admin/goals', p),
        adminGoalFires: (goalId: string, limit = 30) => get<{ fires: AdminGoalFire[] }>(`/admin/goals/${goalId}/fires`, { limit }),
        adminJobs: (p: { org?: string; status?: string; limit?: number; before?: string } = {}) =>
            get<{ jobs: AdminJob[]; next_before: string | null }>('/admin/jobs', p),
        adminJob: (jobId: string) => get<AdminJobDetail>(`/admin/jobs/${jobId}`),
        deleteMemory: (path: string) =>
            t.request<{ path: string; deleted: boolean; changeset_id: string | null }>('DELETE', '/memory/file', { params: { path } }),
    }
}

export type AgentApi = ReturnType<typeof createAgentApi>

export type GoalAutonomy = 'notify' | 'review' | 'standard'

export interface GoalTriggers {
    schedule?: { every_minutes?: number; daily_at?: string; tz?: string; weekdays?: number[] }
    events?: string[]
}

/** A standing goal: work the agent does on its own, on a schedule or when something happens. */
export interface Goal {
    id: string
    kind: string
    title: string
    instruction: string
    config: Record<string, unknown>
    triggers: GoalTriggers
    autonomy: GoalAutonomy
    tier: string
    notify: string[]
    enabled: boolean
    system: boolean
    next_run_at: string | null
    last_run_at: string | null
    last_status: string | null
    last_summary: string | null
    created_at: string
}

export interface GoalFire {
    id: string
    trigger: string
    status: string
    summary: string | null
    run_id: string | null
    session_id: string | null
    created_at: string
    finished_at: string | null
}

export type GoalInput = Partial<Pick<Goal, 'title' | 'instruction' | 'triggers' | 'autonomy' | 'tier' | 'notify' | 'config' | 'enabled'>>

let _api: AgentApi | null = null
/** The creator's IANA timezone, so a browser they start opens in the region nearest them. */
function viewerTimezone(): string | null {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || null
    } catch {
        return null
    }
}

function withTimezone<T extends object>(pane: T): T & { timezone: string | null } {
    return { ...pane, timezone: viewerTimezone() }
}

export function agentApi(): AgentApi {
    if (!_api) _api = createAgentApi()
    return _api
}
/** Tests and the dev playground swap the transport. */
export function setAgentApi(api: AgentApi | null): void {
    _api = api
}
